import type {
  ActiveContext,
  AuthProvider,
  AuthResult,
  AuthSession,
  AuthUser,
  OnboardingPayload,
  Workspace,
} from './types';
import { normalizeEmail, slugify } from './validation';

const DATABASE_KEY = 'nexo.phase2.local.database';
const SESSION_KEY = 'nexo.phase2.local.session';
const TOKEN_LIFETIME = 1000 * 60 * 60 * 12;

type StoredUser = AuthUser & { passwordHash: string; passwordFallbackHash?: string };
type PendingToken = { token: string; userId: string; expiresAt: number; type: 'verify' | 'magic' | 'reset' };
type LocalDatabase = {
  users: StoredUser[];
  workspaces: Record<string, Workspace>;
  pendingTokens: PendingToken[];
};

const createId = (prefix: string) => {
  const random = crypto.randomUUID?.() ?? `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}_${random}`;
};
const now = () => Date.now();

const seedDatabase = (): LocalDatabase => ({
  users: [
    {
      id: 'usr_demo_owner',
      email: 'propietario@nexo.demo',
      firstName: 'Mariana',
      lastName: 'Torres',
      emailVerified: true,
      passwordHash: 'ea59412351f2f08586bb8193971ad3390b1e263b084b81c832d507eb10b46ee5',
      passwordFallbackHash: 'bda6c3ed',
    },
    {
      id: 'usr_demo_admin',
      email: 'admin@nexo.demo',
      firstName: 'NEXO',
      lastName: 'Admin',
      emailVerified: true,
      platformRole: 'superadmin',
      passwordHash: 'ffe0280c773ec9fed4515f3819dc862c39bea6c7ffe3ce38a2a7d99c141b026c',
      passwordFallbackHash: '242d4e94',
    },
  ],
  workspaces: {
    usr_demo_admin: { onboardingComplete: true, organizations: [], businesses: [], branches: [], memberships: [] },
    usr_demo_owner: {
      onboardingComplete: true,
      organizations: [
        { id: 'org_demo_norte', name: 'Grupo Norte', slug: 'grupo-norte', ownerUserId: 'usr_demo_owner' },
      ],
      businesses: [
        {
          id: 'biz_casa_norte', organizationId: 'org_demo_norte', name: 'Casa Norte', slug: 'casa-norte', industry: 'Cafetería y alimentos', profileId: 'restaurant', operationModel: 'physical-store', cashControlEnabled: true,
          phone: '899 100 2026', whatsapp: '528991002026', email: 'hola@casanorte.demo', address: 'Calle Hidalgo 120', city: 'Reynosa',
          state: 'Tamaulipas', country: 'México', postalCode: '88500', currency: 'MXN', timezone: 'America/Monterrey',
          primaryColor: '#163d36', accentColor: '#e49b5d', catalogEnabled: true, catalogSlug: 'casa-norte', catalogShowPrices: true,
          catalogShowAvailability: true, catalogAllowOrders: true, catalogAllowPickup: true, catalogAllowDelivery: true, catalogHideOutOfStock: false, catalogShowExactStock: false,
        },
        {
          id: 'biz_taller_norte', organizationId: 'org_demo_norte', name: 'Taller Norte', slug: 'taller-norte', industry: 'Servicios personalizados', profileId: 'services', operationModel: 'service-area', cashControlEnabled: false,
          phone: '899 210 8080', whatsapp: '528992108080', email: 'pedidos@tallernorte.demo', address: 'Blvd. Morelos 840', city: 'Reynosa',
          state: 'Tamaulipas', country: 'México', postalCode: '88730', currency: 'MXN', timezone: 'America/Monterrey',
          primaryColor: '#263a55', accentColor: '#d58b62', catalogEnabled: true, catalogSlug: 'taller-norte', catalogShowPrices: false,
          catalogShowAvailability: true, catalogAllowOrders: true, catalogAllowPickup: true, catalogAllowDelivery: true, catalogHideOutOfStock: false, catalogShowExactStock: false,
        },
        {
          id: 'biz_venta_movil', organizationId: 'org_demo_norte', name: 'Moda Ruta', slug: 'moda-ruta', industry: 'Ropa por catálogo, apartados y entregas', profileId: 'retail', operationModel: 'mobile-seller', cashControlEnabled: false,
          phone: '899 410 3030', whatsapp: '528994103030', email: 'pedidos@modaruta.demo', address: '', city: 'Reynosa', state: 'Tamaulipas', country: 'México', postalCode: '', currency: 'MXN', timezone: 'America/Monterrey',
          primaryColor: '#4a315f', accentColor: '#d78d6d', catalogEnabled: true, catalogSlug: 'moda-ruta', catalogShowPrices: true,
          catalogShowAvailability: true, catalogAllowOrders: true, catalogAllowPickup: false, catalogAllowDelivery: true, catalogHideOutOfStock: false, catalogShowExactStock: false,
        },
      ],
      branches: [
        { id: 'br_centro', businessId: 'biz_casa_norte', name: 'Sucursal Centro', address: 'Calle Hidalgo 120', phone: '899 100 2026', schedule: 'Lun–Sáb, 8:00–20:00', registerName: 'Caja principal', warehouseName: 'Almacén Centro', operationModel: 'physical-store' },
        { id: 'br_fuentes', businessId: 'biz_casa_norte', name: 'Sucursal Las Fuentes', address: 'Av. Las Fuentes 430', phone: '899 100 2027', schedule: 'Lun–Dom, 9:00–21:00', registerName: 'Caja mostrador', warehouseName: 'Almacén Las Fuentes', operationModel: 'physical-store' },
        { id: 'br_taller', businessId: 'biz_taller_norte', name: 'Zona Principal', address: '', phone: '899 210 8080', schedule: 'Lun–Vie, 9:00–18:00', registerName: 'Cobros', warehouseName: 'Materiales', operationModel: 'service-area' },
        { id: 'br_movil', businessId: 'biz_venta_movil', name: 'Venta móvil y entregas', address: '', phone: '899 410 3030', schedule: 'Horario flexible', registerName: 'Cobros móviles', warehouseName: 'Inventario en resguardo', operationModel: 'mobile-seller' },
      ],
      memberships: [
        { id: 'mem_demo_casa', userId: 'usr_demo_owner', organizationId: 'org_demo_norte', businessId: 'biz_casa_norte', role: 'owner' },
        { id: 'mem_demo_taller', userId: 'usr_demo_owner', organizationId: 'org_demo_norte', businessId: 'biz_taller_norte', role: 'owner' },
        { id: 'mem_demo_movil', userId: 'usr_demo_owner', organizationId: 'org_demo_norte', businessId: 'biz_venta_movil', role: 'owner' },
      ],
      activeContext: { organizationId: 'org_demo_norte', businessId: 'biz_casa_norte', branchId: 'br_centro' },
    },
  },
  pendingTokens: [],
});

const readDatabase = (): LocalDatabase => {
  const stored = localStorage.getItem(DATABASE_KEY);
  if (!stored) {
    const seeded = seedDatabase();
    localStorage.setItem(DATABASE_KEY, JSON.stringify(seeded));
    return seeded;
  }
  try {
    return JSON.parse(stored) as LocalDatabase;
  } catch {
    const seeded = seedDatabase();
    localStorage.setItem(DATABASE_KEY, JSON.stringify(seeded));
    return seeded;
  }
};

const writeDatabase = (database: LocalDatabase) => localStorage.setItem(DATABASE_KEY, JSON.stringify(database));

const fallbackPasswordHash = (password: string) => {
  let hash = 2166136261;
  const value = `nexo-local-v2:${password}`;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(16).padStart(8, '0');
};

const hashPassword = async (password: string) => {
  if (crypto.subtle) {
    const bytes = new TextEncoder().encode(password);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  }
  return fallbackPasswordHash(password);
};


const publicUser = (user: StoredUser): AuthUser => ({
  id: user.id,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  emailVerified: user.emailVerified,
  platformRole: user.platformRole ?? 'user',
});

const createSession = (user: StoredUser): AuthSession => {
  const session: AuthSession = {
    accessToken: createId('local_session'),
    expiresAt: now() + TOKEN_LIFETIME,
    provider: 'local',
    user: publicUser(user),
  };
  localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  return session;
};

const tokenFor = (database: LocalDatabase, userId: string, type: PendingToken['type']) => {
  const token = createId(type);
  database.pendingTokens = database.pendingTokens.filter((item) => !(item.userId === userId && item.type === type));
  database.pendingTokens.push({ token, userId, type, expiresAt: now() + 1000 * 60 * 30 });
  writeDatabase(database);
  return token;
};

const consumeToken = (database: LocalDatabase, token: string, type: PendingToken['type']) => {
  const match = database.pendingTokens.find((item) => item.token === token && item.type === type && item.expiresAt > now());
  if (!match) throw new Error('El enlace ya venció o no es válido. Solicita uno nuevo.');
  database.pendingTokens = database.pendingTokens.filter((item) => item.token !== token);
  const user = database.users.find((item) => item.id === match.userId);
  if (!user) throw new Error('No se encontró la cuenta asociada.');
  return user;
};

const loadWorkspaceFor = (userId: string): Workspace => {
  const database = readDatabase();
  return database.workspaces[userId] ?? {
    onboardingComplete: false,
    organizations: [],
    businesses: [],
    branches: [],
    memberships: [],
  };
};

export class LocalAuthProvider implements AuthProvider {
  mode = 'local' as const;

  async restoreSession(): Promise<AuthSession | null> {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    try {
      const session = JSON.parse(raw) as AuthSession;
      if (session.expiresAt <= now()) {
        localStorage.removeItem(SESSION_KEY);
        return null;
      }
      const database = readDatabase();
      const user = database.users.find((item) => item.id === session.user.id);
      if (!user) return null;
      return { ...session, user: publicUser(user) };
    } catch {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    const database = readDatabase();
    const user = database.users.find((item) => item.email === normalizeEmail(email));
    const primaryHash = await hashPassword(password);
    const fallbackHash = fallbackPasswordHash(password);
    if (!user || (user.passwordHash !== primaryHash && user.passwordFallbackHash !== fallbackHash)) throw new Error('Correo o contraseña incorrectos.');
    return { session: createSession(user) };
  }

  async signUp(input: { email: string; password: string; firstName: string; lastName: string }): Promise<AuthResult> {
    const database = readDatabase();
    const email = normalizeEmail(input.email);
    if (database.users.some((item) => item.email === email)) throw new Error('Ya existe una cuenta con este correo.');
    const user: StoredUser = {
      id: createId('usr'),
      email,
      firstName: input.firstName.trim(),
      lastName: input.lastName.trim(),
      emailVerified: true,
      passwordHash: await hashPassword(input.password),
      passwordFallbackHash: fallbackPasswordHash(input.password),
    };
    database.users.push(user);
    database.workspaces[user.id] = { onboardingComplete: false, organizations: [], businesses: [], branches: [], memberships: [] };
    writeDatabase(database);
    return { session: createSession(user), message: 'Cuenta creada. Tu prueba gratuita está lista.' };
  }

  async signOut(): Promise<void> {
    localStorage.removeItem(SESSION_KEY);
  }

  async requestMagicLink(email: string): Promise<{ message: string; token?: string }> {
    const database = readDatabase();
    const user = database.users.find((item) => item.email === normalizeEmail(email));
    if (!user) return { message: 'Si el correo existe, se preparó un enlace de acceso.' };
    const token = tokenFor(database, user.id, 'magic');
    return { message: 'Enlace de acceso preparado para el modo local.', token };
  }

  async requestPasswordReset(email: string): Promise<{ message: string; token?: string }> {
    const database = readDatabase();
    const user = database.users.find((item) => item.email === normalizeEmail(email));
    if (!user) return { message: 'Si el correo existe, se preparó un enlace de recuperación.' };
    const token = tokenFor(database, user.id, 'reset');
    return { message: 'Enlace de recuperación preparado para el modo local.', token };
  }

  async confirmEmail(token: string): Promise<AuthSession> {
    const database = readDatabase();
    const user = consumeToken(database, token, 'verify');
    user.emailVerified = true;
    writeDatabase(database);
    return createSession(user);
  }

  async consumeMagicLink(token: string): Promise<AuthSession> {
    const database = readDatabase();
    const user = consumeToken(database, token, 'magic');
    if (!user.emailVerified) user.emailVerified = true;
    writeDatabase(database);
    return createSession(user);
  }

  async resetPassword(token: string, password: string): Promise<void> {
    const database = readDatabase();
    const user = consumeToken(database, token, 'reset');
    user.passwordHash = await hashPassword(password);
    user.passwordFallbackHash = fallbackPasswordHash(password);
    writeDatabase(database);
  }

  async loadWorkspace(session: AuthSession): Promise<Workspace> {
    return loadWorkspaceFor(session.user.id);
  }

  async completeOnboarding(session: AuthSession, payload: OnboardingPayload): Promise<Workspace> {
    const database = readDatabase();
    const organizationId = createId('org');
    const businessId = createId('biz');
    const branchId = createId('br');
    const organization = {
      id: organizationId,
      name: payload.organization.name,
      slug: slugify(payload.organization.name),
      ownerUserId: session.user.id,
    };
    const business = { ...payload.business, id: businessId, organizationId };
    const branch = { ...payload.branch, id: branchId, businessId };
    const workspace: Workspace = {
      onboardingComplete: true,
      organizations: [organization],
      businesses: [business],
      branches: [branch],
      memberships: [{ id: createId('mem'), userId: session.user.id, organizationId, businessId, branchId, role: 'owner' }],
      activeContext: { organizationId, businessId, branchId },
    };
    database.workspaces[session.user.id] = workspace;
    const user = database.users.find((item) => item.id === session.user.id);
    if (user) {
      user.firstName = payload.owner.firstName;
      user.lastName = payload.owner.lastName;
    }
    writeDatabase(database);
    const nextSession = user ? { ...session, user: publicUser(user) } : session;
    localStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
    return workspace;
  }

  async switchContext(session: AuthSession, context: ActiveContext): Promise<Workspace> {
    const database = readDatabase();
    const workspace = database.workspaces[session.user.id];
    if (!workspace) throw new Error('No se encontró el espacio de trabajo.');
    const branch = workspace.branches.find((item) => item.id === context.branchId && item.businessId === context.businessId);
    const business = workspace.businesses.find((item) => item.id === context.businessId && item.organizationId === context.organizationId);
    const membership = workspace.memberships.find((item) => item.userId === session.user.id && item.businessId === context.businessId);
    if (!branch || !business || !membership) throw new Error('No tienes acceso a este negocio o sucursal.');
    workspace.activeContext = context;
    writeDatabase(database);
    return workspace;
  }
}
